import React, { ReactElement, useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import '../../assets/css/editor.scss';
import { PreviewThemeParamType } from './routes';

import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import { useGetThemeQuery } from '@/modules/services';
import PlayerUi from '@/components/Player';
import {
  getEntityLocation,
  transformElearningThemeAssetLinks,
  useIsMobileScreenSize,
} from '@/lib/util';
import SettingsBaseRoutes, {
  TenantSettingsTabs,
} from '@/pages/Settings/SettingRoutes';
import { Spinner } from 'elmo-elements';

const templateContent = `<h1>This is heading 1 text</h1>
  
<h2>This is heading 2 text</h2>

<p>this is body text</p>

<p><a class="btn" href="#" style="display:inline-block;padding:5px 10px;border-radius:5px;font-size:14px;text-decoration: none !important; cursor: pointer;">Button</a></p>

<p><a href="#">This is a url link</a></p>

<p>&nbsp;</p>`;

const frameContent = {
  id: '',
  title: 'Theme preview',
  description: 'This is a theme preview frame data.',
  tenantId: '',
  createdAt: '',
  modifiedAt: '',
  isDeleted: null,
  content: `<p>This is desktop content</p> ${templateContent}`,
  mobileContent: `<p>This is mobile content</p> ${templateContent}`,
  js: '',
  templateId: null,
  bgVideo: '',
  bgVideoTitle: '',
  frameMetadata: {
    disabled: 0,
    position: 0,
    swipeNavigation: 1,
    mute: 1,
    bgImage: '',
    backgroundColor: '',
    bgVideo: '',
  },
  lcmsReferenceIds: { id: 0, courseId: 0, templateId: 0 },
  vocamMetadata: {
    regionId: 11,
    channelId: 0,
    moduleId: 0,
    topicId: 0,
  },
  elements: [
    {
      content: 'I am a tooltip',
      createdAt: '2025-02-06T05:37:41.692Z',
      elementMetadata: {
        height: 0,
        position: 1,
        type: 2,
        width: 0,
      },
      id: '199302c4-ac50-4fab-a620-25e01d21c638',
      isDeleted: null,
      lcmsReferenceIds: {},
      modifiedAt: '2025-02-06T05:37:41.692Z',
      name: 'Tooltip',
      tenantId: 'ae03e0b1-0d4a-4e86-a371-af821b42c52c',
    },
  ],
};

const placeHolderElearning = {
  id: '',
  name: 'Theme preview elearning',
  tags: '',
  tenant: {
    tenantId: '',
    namespace: '',
    subdomain: '',
    identifier: '',
    createdAt: '',
    modifiedAt: '',
    isDeleted: null,
  },
  tenantId: '',
  createdAt: '',
  modifiedAt: '',
  isDeleted: null,
  description: '',
  version: 1,
  units: '',
  theme: undefined,
  lcmsReferenceIds: {
    id: 0,
    encryptedId: '',
    companyId: 0,
    departmentId: 0,
    categoryId: 0,
    parentCourseId: 0,
    themeId: 0,
  },
  elearningMetadata: {
    isLibrary: 0,
    isVideoLibrary: 0,
    isFullService: 0,
    isDemo: 1,
    isEnabled: 1,
    isMigrated: 0,
    newUntil: '',
  },
  playerMetadata: {
    mute: 0,
    showResource: 1,
    showMenu: 1,
    menu: '',
    menuX: 5,
    menuY: 73,
    voiceMessage: '',
    playerType: 1,
  },
  titleMetadata: {
    posX: 570,
    posY: 36,
    nameFont: 'arial,helvetica,sans-serif',
    nameSize: '1.3em',
    nameColor: '000000',
  },
  multiLanguageMetadata: {
    multiLang: '',
    multiLangX: 5,
    multiLangY: 5,
  },
  statusMetadata: {
    status: 1,
    lmsStatus: 0,
    scormStatus: 0,
    breatheStatus: 0,
    courseSync: 0,
    lmsSync: 0,
    scormSync: 0,
    breatheSync: 0,
  },
  toolTip: 0,
  bgImageName: '',
  parentElearningId: '',
  categoryId: '',
  reviewers: [],
  resources: [],
};

const PreviewTheme = (): ReactElement => {
  const history = useHistory();
  const { themeId } = useParams<PreviewThemeParamType>();
  const { data: themeData, isLoading, isSuccess } = useGetThemeQuery(themeId);

  const { isMobile } = useIsMobileScreenSize();

  const frameData = {
    ...frameContent,
    id: themeId,
  };
  const themeLocation = getEntityLocation({
    tenantId: `${themeData?.tenantId}`,
    entity: 'themes',
    entityId: `${themeData?.id}`,
  });
  const elearningData = {
    ...placeHolderElearning,
    theme: themeData
      ? transformElearningThemeAssetLinks(
          themeData,
          `${themeLocation}/pages/images`,
        )
      : undefined,
  };

  useThemeStyle(elearningData.theme);

  useEffect(() => {
    // Redirect back to theme list - themeId is not valid
    if (isSuccess && !themeData) {
      history.replace(
        generatePath(SettingsBaseRoutes.path, {
          tabSelected: TenantSettingsTabs.Theme,
        }),
      );
    }
  }, [history, isSuccess, themeData]);

  if (isLoading || !themeData) {
    return <Spinner />;
  }

  return (
    <PlayerUi
      isPreview={true}
      isPlayable={true}
      currentFrameId={themeId}
      elearning={elearningData}
      isQuizEnabled={false}
      frames={[frameData]}
      resources={[]}
      helperContent={[]}
      helperFaq={[]}
      isMobileView={isMobile}
    />
  );
};

export default PreviewTheme;
